import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { formatDayText } from '../events/fields';
import { useUrlState } from '../nav/urlState';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { FORMS, formText, METHODS, PAYMENTS_KEY, RECEIVABLES_RESOURCE, stateLabel, type Form, type ToConfirm } from './types';

const money = (g: number | undefined) => (g === undefined ? '—' : formatPln(g));
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

/**
 * "Finanse → Płatności → Do potwierdzenia" (owner 2026-10-22 01:38): the receivables we wait for, of every settlement
 * form, by due date; filters by form and "Po terminie". "Potwierdź" books a payment typed by hand (part of it too);
 * "Cofnij" deletes it again. Receivables confirmed in full stay listed for a while, to undo.
 */
export function ToConfirmPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const { params, update } = useUrlState();
  const form = (Object.keys(FORMS) as Form[]).find((f) => f === params.get('forma')) ?? null;
  const overdue = params.get('po-terminie') === '1';
  const [confirming, setConfirming] = useState<ToConfirm | null>(null);
  const search = new URLSearchParams({ ...(form ? { form } : {}), ...(overdue ? { overdue: '1' } : {}) });
  const list = useQuery({ queryKey: [...PAYMENTS_KEY, 'to-confirm', form, overdue], queryFn: () => api<{ receivables: ToConfirm[] }>(`/api/payments/to-confirm?${search.toString()}`) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
  const undo = useMutation({
    mutationFn: (allocationId: string) => api(`/api/payments/allocations/${allocationId}?reason=${encodeURIComponent('cofnięte w „Do potwierdzenia”')}`, { method: 'DELETE' }),
    onSettled: refresh,
  });
  if (!me) return null;
  const rows = list.data?.receivables ?? [];
  const amounts = fieldVisible(me, RECEIVABLES_RESOURCE, 'remainingGrosze');
  const recording = can(me, RECEIVABLES_RESOURCE, 'create') && fieldVisible(me, RECEIVABLES_RESOURCE, 'amountGrosze');
  return (
    <div data-feature-id="payments.confirm">
      <div className="pay-confirm-filters">
        <label className="field">
          <span>Forma rozliczenia</span>
          <select aria-label="Forma rozliczenia" value={form ?? ''} onChange={(e) => update({ forma: e.target.value || null })}>
            <option value="">Wszystkie</option>
            {(Object.keys(FORMS) as Form[]).map((f) => (
              <option key={f} value={f}>
                {FORMS[f]}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={overdue} onChange={(e) => update({ 'po-terminie': e.target.checked ? '1' : null })} /> Po terminie
        </label>
      </div>
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać płatności')}</p>}
      {undo.error && <p className="alert" role="alert">{errorText(undo.error, 'Nie udało się cofnąć potwierdzenia')}</p>}
      {list.data && rows.length === 0 && <p className="muted">{overdue ? 'Brak płatności po terminie.' : 'Brak płatności do potwierdzenia.'}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table pay-table" aria-label="Płatności do potwierdzenia">
            <thead>
              <tr>
                <th scope="col">Termin</th>
                <th scope="col">Należność</th>
                <th scope="col">Klient</th>
                <th scope="col" className="wide-only">
                  Wydarzenie
                </th>
                {amounts && (
                  <th scope="col" className="num">
                    Pozostało
                  </th>
                )}
                <th scope="col">Stan</th>
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">{formatDayText(r.dueDate)}</td>
                  <td>
                    {r.label}
                    <span className="pay-sub">{formText(r) || METHODS[r.method]}</span>
                    {r.confirmations.length > 0 && (
                      <ul className="pay-confirmations" aria-label="Potwierdzone wpłaty">
                        {r.confirmations.map((c) => (
                          <li key={c.id}>
                            <span>
                              Potwierdzono {money(c.amountGrosze)} · {formatDayText(c.paidOn)}
                              {c.note ? ` · ${c.note}` : ''}
                            </span>
                            {recording && (
                              <button className="btn ghost small" type="button" aria-label={`Cofnij potwierdzenie z ${formatDayText(c.paidOn)}`} disabled={undo.isPending} onClick={() => undo.mutate(c.id)}>
                                Cofnij
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>{r.contractorName || <span className="muted">bez klienta</span>}</td>
                  <td className="wide-only">{r.eventId ? <Link to={`/wydarzenia?wydarzenie=${r.eventId}&zakladka=platnosci`}>{r.eventName}</Link> : null}</td>
                  {amounts && <td className="num nowrap">{money(r.remainingGrosze)}</td>}
                  <td>
                    <span className="pay-state" data-state={r.overdueDays > 0 ? 'zalegla' : r.state}>
                      {stateLabel(r)}
                    </span>
                  </td>
                  <td className="q-actions">
                    {recording && r.state === 'otwarta' && (
                      <button className="btn small" type="button" onClick={() => setConfirming(r)}>
                        Potwierdź
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirming && (
        <RecordPaymentDialog
          receivable={confirming}
          title="Potwierdź płatność"
          featureId="payments.confirm"
          onClose={() => setConfirming(null)}
          onSaved={() => {
            setConfirming(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
